import { Chart } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { RecentAnswer } from "winslow-core";
import { toMMDD, convertUTCtoLocal } from "winslow-core";

ChartJS.register(...registerables);

// start and end need to be in PST
type SummaryChartProps = {
  responses: RecentAnswer[];
  startDate: Date;
  endDate: Date;
  period?: "daily" | "monthly";
};

export const SummaryChart = ({
  responses,
  startDate,
  endDate,
  period = "daily",
}: SummaryChartProps) => {
  const getBucket = (forDate: Date): number => {
    const daysApart = calculateDaysBetweenDates(startDate, forDate);
    return daysApart;
  };

  function calculateDaysBetweenDates(date1: Date, date2: Date) {
    // Convert both dates to milliseconds
    const date1Millis = date1.getTime();
    const date2Millis = date2.getTime();

    // Calculate the difference in milliseconds
    const differenceMillis = date2Millis - date1Millis;

    // Convert milliseconds to days
    const differenceDays = differenceMillis / (1000 * 60 * 60 * 24);

    // Round to handle any daylight saving time issues
    const diffDays = Math.round(differenceDays);

    return diffDays;
  }

  const getLabelsForDateRange = () => {
    const rangeDays = calculateDaysBetweenDates(startDate, endDate);
    const labels: string[] = [];

    for (let i = 0; i <= rangeDays; i++) {
      let newDate = new Date(startDate);
      newDate.setDate(startDate.getDate() + i);
      labels.push(toMMDD(newDate));
    }

    return labels;
  };

  const getSeriesDataForCategory = (
    buckets: Map<number, RecentAnswer[]>,
    category?: string
  ) => {
    const rangeDays = calculateDaysBetweenDates(startDate, endDate);
    const data: number[] = [];

    for (let i = 0; i <= rangeDays; i++) {
      const responses = buckets.get(i);

      if (responses) {
        let count = 0;
        responses.forEach((response: RecentAnswer) => {
          if (!category || response.category === category) {
            count++;
          }
        });
        data.push(count);
      } else {
        data.push(0);
      }
    }

    return data;
  };

  const getDataSets = (buckets: Map<number, RecentAnswer[]>) => {
    const datasets = [
      {
        label: "Questions",
        data: getSeriesDataForCategory(buckets),
        fill: false,
        backgroundColor: "rgba(75,192,192,0.2)",
        borderColor: "rgba(75,192,192,1)",
      },
    ];

    return datasets;
  };

  // sort by date oldest to newest
  responses.sort((a: RecentAnswer, b: RecentAnswer) => {
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  // put all the responses into their buckets
  const buckets: Map<number, RecentAnswer[]> = new Map<
    number,
    RecentAnswer[]
  >();

  responses.forEach((response: RecentAnswer, i: number) => {
    const bucket = getBucket(convertUTCtoLocal(new Date(response.createdAt)));

    if (!buckets.has(bucket)) {
      buckets.set(bucket, [] as RecentAnswer[]);
    }

    const bucketResponses = buckets.get(bucket);
    bucketResponses?.push(response);

    if (bucketResponses) {
      buckets.set(bucket, bucketResponses);
    }
  });

  const data = {
    labels: getLabelsForDateRange(),
    datasets: getDataSets(buckets),
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "Questions Asked L30",
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
      legend: {
        display: false,
        position: "top" as const,
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
        ticks: {
          autoSkip: false,
          maxRotation: 90 as const,
          minRotation: 90 as const,
        },
      },
      y: {
        stacked: true,
      },
      // xAxes: [
      //     {
      //
      //     }
      // ]
    },
  };

  // scales: {
  //     xAxes: [{
  //         ticks: {
  //             autoSkip: false,
  //             maxRotation: 90,
  //             minRotation: 90
  //         }
  //     }]
  // }

  return (
    <>
      <Chart type="bar" height="300px" data={data} options={options} />
    </>
  );
};
